"use client";

import { debounce } from "lodash";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import React, { useState, useEffect, useCallback } from "react";

import SearchInput from "@/components/search_input";
import {
  POST_ORDER_BY_FILTER,
  POST_TEXT_SEARCH_FILTER,
} from "@/constants/posts_feed";
import { useGlobalSearchContext } from "@/contexts/global_search_context";
import { QuestionOrder } from "@/types/question";
import { sendAnalyticsEvent } from "@/utils/analytics";
import cn from "@/utils/core/cn";
import { encodeQueryParams } from "@/utils/navigation";

interface GlobalSearchProps {
  className?: string;
  isMobile?: boolean;
  onSubmit?: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  className,
  isMobile = false,
  onSubmit,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const [isHidden, setIsHidden] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAnalyticsEvent = useCallback(
    debounce(() => {
      sendAnalyticsEvent("feedSearch", {
        event_category: "fromNavbar",
      });
    }, 2000),
    []
  );

  const {
    globalSearch,
    updateGlobalSearch,
    isVisible: otherSearchIsVisible,
    isSearched,
    setIsSearched,
  } = useGlobalSearchContext();

  useEffect(() => {
    setIsHidden(otherSearchIsVisible);
  }, [otherSearchIsVisible]);

  const eraseSearch = () => {
    updateGlobalSearch("");
    setIsSearched(false);
  };

  const handleSearchSubmit = (searchQuery: string) => {
    debouncedAnalyticsEvent();
    onSubmit?.();
    setIsSearched(true);
    router.push(
      `/questions` +
        encodeQueryParams({
          [POST_TEXT_SEARCH_FILTER]: searchQuery,
          [POST_ORDER_BY_FILTER]: QuestionOrder.RankDesc,
        })
    );
  };

  const visibilityClass = isMobile
    ? "flex md:hidden"
    : isHidden
      ? "hidden"
      : "hidden md:flex";

  return (
    <div
      className={cn(
        "ml-2.5 items-center self-center xl:items-center",
        visibilityClass,
        className
      )}
    >
      <SearchInput
        value={globalSearch}
        onChange={(e) => updateGlobalSearch(e.target.value)}
        onErase={eraseSearch}
        onSubmit={handleSearchSubmit}
        placeholder={t("questionSearchPlaceholder")}
        size="base"
        className="w-full min-w-[220px]"
        inputClassName={cn(
          "text-gray-800",
          "bg-gray-100 hover:bg-gray-200 focus:bg-white",
          "border border-gray-300 hover:border-gray-400 focus:border-blue-700",
          "outline-none focus:outline-1 focus:outline-offset-0 focus:outline-blue-700",
          "placeholder:text-gray-500 focus:placeholder:text-gray-400",
          { "bg-gray-200": isSearched }
        )}
        eraseButtonClassName="text-gray-500 hover:text-gray-700"
        submitButtonClassName="hidden md:block"
        submitIconClassName="text-gray-500"
      />
    </div>
  );
};

export default GlobalSearch;
